/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import { EditableText } from '../Editable.js';
import { inputStyle, monoLabel } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

export function Projects() {
  const utils = trpc.useUtils();
  const list = trpc.projects.list.useQuery();
  const create = trpc.projects.create.useMutation({
    onSuccess: async () => {
      await utils.projects.list.invalidate();
    },
  });
  const complete = trpc.projects.complete.useMutation({
    onSuccess: async () => {
      await utils.projects.list.invalidate();
    },
  });
  const drop = trpc.projects.drop.useMutation({
    onSuccess: async () => {
      await utils.projects.list.invalidate();
    },
  });
  const update = trpc.projects.update.useMutation({
    onSuccess: async () => {
      await utils.projects.list.invalidate();
    },
  });

  const [draftTitle, setDraftTitle] = useState('');
  const [draftOutcome, setDraftOutcome] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const title = draftTitle.trim();
    if (!title) return;
    const outcome = draftOutcome.trim();
    create.mutate(
      { title, desiredOutcome: outcome === '' ? null : outcome },
      {
        onSuccess: () => {
          setDraftTitle('');
          setDraftOutcome('');
        },
      },
    );
  };

  const items = list.data ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ ...monoLabel }}>Projects · in flight</div>

      {list.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : items.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 15,
            color: p.muted,
            margin: 0,
          }}
        >
          No projects in flight.
        </p>
      ) : (
        <div>
          {items.map((pr) => (
            <ProjectRow
              key={pr.id}
              title={pr.title}
              desiredOutcome={pr.desiredOutcome}
              onComplete={() => complete.mutate({ projectId: pr.id })}
              onDrop={() => drop.mutate({ projectId: pr.id })}
              onEditTitle={(next) => update.mutate({ projectId: pr.id, title: next })}
              onEditOutcome={(next) =>
                update.mutate({ projectId: pr.id, desiredOutcome: next === '' ? null : next })
              }
              pending={complete.isPending || drop.isPending || update.isPending}
            />
          ))}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Project title"
            aria-label="Project title"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="submit"
            disabled={create.isPending || draftTitle.trim() === ''}
            style={{
              padding: '10px 18px',
              background: p.fg,
              color: p.bg,
              border: 'none',
              fontSize: 13,
              cursor: create.isPending || draftTitle.trim() === '' ? 'not-allowed' : 'pointer',
              opacity: create.isPending || draftTitle.trim() === '' ? 0.6 : 1,
            }}
          >
            {create.isPending ? 'Adding…' : 'Add →'}
          </button>
        </div>
        <input
          type="text"
          value={draftOutcome}
          onChange={(e) => setDraftOutcome(e.target.value)}
          placeholder="Desired outcome (optional) — what does done look like?"
          aria-label="Desired outcome"
          style={{ ...inputStyle, fontStyle: 'italic' }}
        />
      </form>
      {create.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{create.error.message}</span>
      ) : null}
    </section>
  );
}

function ProjectRow({
  title,
  desiredOutcome,
  onComplete,
  onDrop,
  onEditTitle,
  onEditOutcome,
  pending,
}: {
  title: string;
  desiredOutcome: string | null;
  onComplete: () => void;
  onDrop: () => void;
  onEditTitle: (next: string) => void;
  onEditOutcome: (next: string) => void;
  pending: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'start',
        columnGap: 12,
        rowGap: 4,
        padding: '12px 0',
        borderTop: `1px solid ${p.line}`,
      }}
    >
      <button
        type="button"
        onClick={onComplete}
        disabled={pending}
        title="Mark complete"
        aria-label="Mark complete"
        style={{
          width: 18,
          height: 18,
          border: `1px solid ${p.line}`,
          background: 'transparent',
          cursor: pending ? 'wait' : 'pointer',
          marginTop: 2,
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <EditableText
          value={title}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === '' || trimmed === title) return;
            onEditTitle(trimmed);
          }}
          ariaLabel="Edit project title"
          renderStyle={{ fontSize: 15, color: p.fg, fontWeight: 500 }}
          inputStyle={{ ...inputStyle, fontSize: 15, padding: '6px 8px' }}
        />
        <EditableText
          value={desiredOutcome ?? ''}
          onCommit={(next) => {
            const trimmed = next.trim();
            if (trimmed === (desiredOutcome ?? '')) return;
            onEditOutcome(trimmed);
          }}
          ariaLabel="Edit desired outcome"
          renderStyle={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 14,
            color: p.muted,
          }}
          inputStyle={{ ...inputStyle, fontSize: 14, padding: '6px 8px', fontStyle: 'italic' }}
          placeholder="Add a desired outcome"
        />
      </div>
      <button
        type="button"
        onClick={onDrop}
        disabled={pending}
        title="Drop"
        aria-label="Drop"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: pending ? 'wait' : 'pointer',
          fontSize: 16,
          padding: '0 4px',
          marginTop: 2,
        }}
      >
        ×
      </button>
    </div>
  );
}
